import React from 'react';
import PropTypes from 'prop-types';
import { MODES } from 'constants';
import mediator, { ACTIONS } from 'mediator';
import classnames from 'classnames';
import get from 'lodash/get';
import API from 'services/API';
import logger from 'lib/common/logger';
import fetcher from 'lib/common/fetcher';
import features from 'lib/common/features';
import arrayWrap from 'lib/common/arrayWrap';
import packages from 'lib/cart/packages';
import panToElement from 'lib/seatmaps/panToElement';
import preselectLevel from 'lib/seatmaps/preselectLevel';
import preselectScreen from 'lib/seatmaps/preselectScreen';
import generateSeatMap from 'lib/seatmaps/generateSeatMap';
import combineSubLineItems from 'lib/cart/combineSubLineItems';
import filterPastPerformances from 'lib/common/filterPastPerformances';
import { redirectWithError } from 'lib/common/fallbacks';
import fetchSeatViews from 'lib/seats/fetchSeatViews';

/**
 * Container components
 * @see https://medium.com/@dan_abramov/smart-and-dumb-components-7ca2f9a7c7d0
 */
import SeatMapContainer from 'containers/SeatMapContainer';
import DetailsContainer from 'containers/DetailsContainer';
import PanelContainer from 'containers/PanelContainer';

/**
 * Presentational Components
 * @see https://medium.com/@dan_abramov/smart-and-dumb-components-7ca2f9a7c7d0
 */
import Details from 'components/Details';
import BestAvailableLink from 'components/BestAvailableLink';
import PromoCode from 'components/PromoCode';
import PerformanceSelector from 'components/PerformanceSelector';
import PackageSelector from 'components/PackageSelector';
import LevelSelector from 'components/LevelSelector';
import ScreenSelector from 'components/ScreenSelector';
import PriceKey from 'components/PriceKey';
import SeatKey from 'components/SeatKey';
import MiniMap from 'components/MiniMap';
import LevelIndicator from 'components/LevelIndicator';
import BoxMinimumKey from 'components/BoxMinimumKey';

const SYOSContainer = React.createClass({
  propTypes: {
    performanceId: PropTypes.number.isRequired,
    packageNo: PropTypes.number,
    maxTickets: PropTypes.number,
    loginInfo: PropTypes.object.isRequired,
    details: PropTypes.object,
    prices: PropTypes.array,
    seatData: PropTypes.array,
    screenData: PropTypes.array,
    screenZoneData: PropTypes.object,
    selectedLevel: PropTypes.object,
    currentFacility: PropTypes.object,
    usesGeneratedSeatmap: PropTypes.bool,
    activePromoCode: PropTypes.object,
    onChangePerformance: PropTypes.func.isRequired,
    onPromoCodeReady: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
    leaveSingleSeats: PropTypes.string
  },
  getInitialState: function() {
    return {
      currentPerformanceId: this.props.performanceId,
      currentCart: null,
      currentCartItems: null,
      currentFixedPackage: null,
      currentFlexPackage: null,
      isFixedPackagePerformance: false,
      maxTickets: this.props.maxTickets,
      svgData: null,
      performances: [],
      packages: [],
      levels: [],
      zonesByLevel: null,
      seatViews: null,
      isEnhanced: false,
      levelSelected: false,
      selectedScreenId: '1',
      panZoomInstance: null,
      mappedSeatsBySection: null,
      isExchange: false,
      exchangeRules: { current: [], extra: [] },
      isUpgrade: false,
      upgradeRules: { current: [], extra: [] },
      exchangeItemsAmount: 0,
      exchangePriceTypes: []
    };
  },
  componentDidMount: function() {
    this.fetchSeatMap(this.props.loginInfo).then(() => {
      let performanceId = this.state.currentPerformanceId;
      this.fetchPerformance(performanceId);
      this.fetchPackages(this.props.packageNo);
    });

    this.fetchSeatViews();
  },
  componentWillReceiveProps: function(nextProps) {
    var shouldGenerateSeatMap =
      !this.state.isEnhanced &&
      nextProps.usesGeneratedSeatmap &&
      nextProps.seatData.length;

    if (shouldGenerateSeatMap) {
      let svgData;
      try {
        svgData = generateSeatMap(nextProps.seatData);
      } catch (e) {
        redirectWithError(new Error('Failed to create seat-map'));
      }

      this.setState({
        svgData: svgData
      });
    }
  },
  getMode: function() {
    let isFixedPackage = !!this.props.packageNo;
    let isFixedPackagePerformance = !!this.state.isFixedPackagePerformance;
    let isFlexPackagePerformance = !!this.state.currentFlexPackage;

    let mode;

    if (isFixedPackage) {
      mode = MODES.FIXED_PACKAGE;
    } else if (isFixedPackagePerformance) {
      mode = MODES.FIXED_PACKAGE_PERFORMANCE;
    } else if (isFlexPackagePerformance) {
      mode = MODES.FLEX_PACKAGE_PERFORMANCE;
    } else {
      mode = MODES.PERFORMANCE;
    }

    return mode;
  },
  onChangePerformance: function(performanceId, refreshSeatData) {
    this.props.onChangePerformance(performanceId, refreshSeatData);

    // Store a reference to previous panZoom state
    let oldPanZoom = this.state.panZoomInstance;

    if (!oldPanZoom) {
      return;
    }

    let currentPan = oldPanZoom.getPan();
    let currentZoom = oldPanZoom.getZoom();

    /**
         * Reset only the state that needs to
         * change when switching performance.
         */
    this.setState({
      currentPerformanceId: performanceId,
      currentCart: null,
      currentCartItems: null,
      svgData: null,
      facility: null,
      zonesByLevel: null,
      seatViews: null,
      isEnhanced: false,
      selectedScreenId: null,
      panZoomInstance: null
    });

    /**
         * Re-fetch seat-map.
         *
         * As we modify the seat-map DOM the simplest way to
         * reset it is to completely re-render the seat-map.
         *
         * This is quick so it preferable to the complex
         * state-management needed to reset the existing seat-map.
         */
    this.fetchSeatMap(this.props.loginInfo).then(() => {
      this.fetchSeatViews();
      this.fetchPerformance(performanceId);

      // Reset pan position and zoom level.
      let newPanZoom = this.state.panZoomInstance;
      if (newPanZoom) {
        newPanZoom.zoom(currentZoom);
        newPanZoom.pan(currentPan);
      }
    });
  },

  onChangePackage: function(packageId) {
    let url = window.location.href;

    window.location.href = url.replace(this.props.packageNo, packageId);
  },

  fetchPackages: function(packageId) {
    let { loginInfo } = this.props;

    if (features.isEnabled('packageSelector')) {
      API.getPackageSelector(packageId, loginInfo.MOS).then(result => {
        let packageSelectorPackages = result;
        if (packageSelectorPackages.length) {
          this.setState({
            packages: packageSelectorPackages
          });
        }
      });
    }
  },

  fetchSeatMap: function() {
    if (this.props.currentFacility) {
      const currentFacility = this.props.currentFacility;

      return fetcher.fetchHtml(currentFacility.svg).then(svgData => {
        this.setState({
          svgData: svgData,
          facility: currentFacility,
          levelSelected: true,
          seatMapReady: true
        });

        return svgData;
      });
    } else {
      return Promise.resolve(null);
    }
  },
  fetchSeatViews: function() {
    let endpoint = features.getOption('viewFromSeat', 'endpoint');
    if (endpoint) {
      fetchSeatViews(endpoint, this.props.currentFacility).then(seatViews => {
        if (seatViews) {
          this.setState({ seatViews: seatViews });
        }
      });
    }
  },
  fetchPerformance: function(performanceId) {
    let { loginInfo } = this.props;

    /**
         * We only need a list of all available performances
         * in a run if the performance selector is enabled.
         * If the feature is disabled we can save an API call.
         */
    if (features.isEnabled('performanceSelector') && performanceId) {
      API.getProductionDetail(performanceId, loginInfo.MOS).then(result => {
        let performances = arrayWrap(get(result, 'Performance'));
        if (performances.length) {
          this.setState({
            performances: filterPastPerformances(performances)
          });
        }
      });
    }
  },
  onScreenSelected: function(screenId) {
    this.setState({
      levelSelected: !this.state.levelSelected ? true : false,
      selectedScreenId: screenId
    });

    mediator.publish(ACTIONS.SCREEN_FILTER_SELECTED, {
      selected: screenId
    });
  },
  mapSeatsBySection: function(seats) {
    let mappedSeats = {};
    seats.forEach(seat => {
      if (!(seat.data.section in mappedSeats)) {
        mappedSeats[seat.data.section] = [];
      }

      if (
        seat.data.seat_status.status_code === 'AVL' ||
        seat.data.seat_status.status_code === 'HBR' ||
        seat.data.seat_status.status_code === 'LCK'
      ) {
        mappedSeats[seat.data.section] = [
          ...mappedSeats[seat.data.section],
          seat.data.seat_no
        ];
      }
    });

    this.setState({
      mappedSeatsBySection: mappedSeats
    });
  },
  onSeatMapReady: function(collectedSeats, zonesByLevel, levels) {
    const perfNo = this.state.currentPerformanceId;
    const packageNo = this.props.packageNo;

    let states = {
      isEnhanced: true
    };

    if (zonesByLevel) {
      states.zonesByLevel = zonesByLevel;
    }

    if (levels) {
      states.levels = levels;
    }

    features.isEnabled('podSeats') && this.mapSeatsBySection(collectedSeats);
    this.setState(states);

    /**
         * Fetch existing cart
         */
    API.getCart(perfNo).then(
      currentCart => {
        let cart = currentCart || null;

        let cartItems = cart
          ? combineSubLineItems(cart, perfNo, packageNo)
          : [];
        let packageItems = arrayWrap(get(cart, 'PackageLineItem'));

        let currentFixedPackage = null;
        if (packageItems.length) {
          currentFixedPackage = packages.getCurrentFixedPackage(
            packageItems,
            packageNo
          );
        }

        let currentFlexPackage = null;
        if (packageItems.length && perfNo) {
          currentFlexPackage = packages.getCurrentFlexPackage(
            packageItems,
            perfNo
          );
        }

        let isFixedPackagePerformance = false;
        if (packageItems.length && perfNo) {
          let fixedPackage = packages.getCurrentFixedPackagePerf(packageItems);
          isFixedPackagePerformance = !!fixedPackage;
        }

        this.setState({
          currentCart: cart,
          currentCartItems: cartItems,
          currentFixedPackage: currentFixedPackage,
          currentFlexPackage: currentFlexPackage,
          isFixedPackagePerformance: isFixedPackagePerformance
        });

        let data = {};
        if (cart && cart.isExchange) {
          data.isExchange = cart.isExchange;
          data.exchangeRules =
            cart.exchangeRules !== undefined
              ? cart.exchangeRules
              : this.state.exchangeRules;
          data.exchangeItemsAmount =
            cart.exchangeItemsAmount[this.props.details.summary.prod_season_no];
          data.exchangePriceTypes = cart.exchangePriceTypes;

          if (cart.isUpgrade) {
            data.isUpgrade = cart.isUpgrade;
            data.upgradeRules =
              cart.upgradeRules !== undefined
                ? cart.upgradeRules
                : this.state.upgradeRules;
            data.exchangeItemsAmount = Object.values(
              cart.exchangeItemsAmount
            )[0];
          } else {
            data.maxTickets = cart.exchangeItemsAmount;
          }

          this.setState(data);
        }

        /**
             * If we are editing a fixed package at the package level
             * restrict the user to only adding the number of seats
             * that are in their package.
             */
        if (packageNo && cartItems.length) {
          if (!cart.isExchange && !cart.isUpgrade) {
            this.setState({
              maxTickets: cartItems.length
            });
          }
        }

        if (this.props.usesGeneratedSeatmap && this.props.screenZoneData) {
          let selectedScreenNo = preselectScreen(
            cartItems,
            this.props.screenZoneData
          );

          if (selectedScreenNo) {
            this.onScreenSelected(selectedScreenNo);
          }
        } else {
          preselectLevel(cartItems, this.props.seatData);
        }

        this.setState({
          seatMapReady: true
        });
      },
      () => {
        logger.captureMessage('Failed to get cart');

        if (this.props.usesGeneratedSeatmap) {
          this.setState({
            seatMapReady: true
          });
        }

        this.setState({
          currentCart: false,
          cartItems: []
        });
      }
    );
  },
  onPanZoomInit: function(panZoomInstance) {
    this.setState({
      panZoomInstance: panZoomInstance
    });

    var selectedLevel = this.props.selectedLevel;
    if (selectedLevel && features.isEnabled('levelSelector')) {
      // Turning this off for Dr P.
    }
  },

  render: function() {
    const mode = this.getMode();

    const shouldRenderMiniMap =
      features.isEnabled('miniMap') &&
      !this.props.usesGeneratedSeatmap &&
      this.state.panZoomInstance &&
      (window.matchMedia && window.matchMedia('(min-width: 30em)').matches);

    return (
      <div
        className={classnames(
          'syos',
          `syos--facility-${get(
            this.props.currentFacility,
            'facility_no',
            'unknown'
          )}`
        )}
      >
        <div className="syos__body">
          <div className="syos__canvas">
            {this.state.svgData ? (
              <SeatMapContainer
                usesGeneratedSeatmap={this.props.usesGeneratedSeatmap}
                seatMapReady={this.state.seatMapReady}
                svgData={this.state.svgData}
                facility={this.state.facility}
                seatData={this.props.seatData}
                isEnhanced={this.state.isEnhanced}
                selectedLevel={this.props.selectedLevel}
                onPanZoomInit={this.onPanZoomInit}
                onSeatMapReady={this.onSeatMapReady}
                prices={this.props.prices}
              />
            ) : null}

            {!!this.props.prices.length &&
            !!this.props.seatData.length &&
            this.state.currentCartItems !== null ? (
              <DetailsContainer
                mode={mode}
                onComplete={this.props.onComplete}
                prices={this.props.prices}
                seatData={this.props.seatData}
                seatViews={this.state.seatViews}
                maxTickets={this.state.maxTickets}
                currentCart={this.state.currentCart}
                currentCartItems={this.state.currentCartItems}
                currentFixedPackage={this.state.currentFixedPackage}
                currentFlexPackage={this.state.currentFlexPackage}
                isFixedPackagePerformance={this.state.isFixedPackagePerformance}
                performances={this.state.performances}
                currentPerformanceId={this.state.currentPerformanceId}
                currentPackageNo={this.props.packageNo}
                loginInfo={this.props.loginInfo}
                activePromoCode={this.props.activePromoCode}
                mainPanZoomInstance={this.state.panZoomInstance}
                onChangePerformance={this.onChangePerformance}
                leaveSingleSeats={this.props.leaveSingleSeats}
                mappedSeatsBySection={this.state.mappedSeatsBySection}
                isExchange={this.state.isExchange}
                exchangeRules={this.state.exchangeRules}
                isUpgrade={this.state.isUpgrade}
                upgradeRules={this.state.upgradeRules}
                exchangeItemsAmount={this.state.exchangeItemsAmount}
                exchangePriceTypes={this.state.exchangePriceTypes}
              />
            ) : null}

            <PanelContainer>
              <Details
                details={this.props.details}
                isAvailable={features.isEnabled('details')}
              />
              <BestAvailableLink
                currentPerformanceId={this.state.currentPerformanceId}
                isAvailable={!!this.state.currentPerformanceId}
              />
              <PromoCode
                loginInfo={this.props.loginInfo}
                onPromoCodeReady={this.props.onPromoCodeReady}
                selectedLevel={this.props.selectedLevel}
                isAvailable={features.isEnabled('promoCode')}
              />
              <PerformanceSelector
                performances={this.state.performances}
                currentPerformanceId={this.state.currentPerformanceId}
                onChangePerformance={this.onChangePerformance}
                isAvailable={
                  features.isEnabled('performanceSelector') &&
                  mode === MODES.PERFORMANCE
                }
              />

              <PackageSelector
                packages={this.state.packages}
                currentPackageId={this.props.packageNo}
                onChangePackage={this.onChangePackage}
                isAvailable={features.isEnabled('packageSelector')}
              />

              <LevelSelector
                levels={this.state.levels}
                selectedLevel={this.props.selectedLevel}
                isAvailable={
                  !this.props.usesGeneratedSeatmap &&
                  this.state.levels.length > 1
                }
              />
              <LevelIndicator
                levels={this.state.levels}
                selectedLevel={this.props.selectedLevel}
                currentFacility={this.props.currentFacility}
                isAvailable={get(
                  this.props.currentFacility,
                  'levelMapping.enabled'
                )}
              />
              <ScreenSelector
                screens={this.props.screenData}
                screenZoneData={this.props.screenZoneData}
                isAvailable={
                  this.props.usesGeneratedSeatmap && !!this.props.screenZoneData
                }
                selectedScreenId={this.state.selectedScreenId}
                onScreenSelected={this.onScreenSelected}
              />
              <MiniMap
                mainPanZoomInstance={this.state.panZoomInstance}
                currentFacility={this.props.currentFacility}
                isAvailable={shouldRenderMiniMap}
              />
              <PriceKey
                prices={this.props.prices}
                screenZoneData={this.props.screenZoneData}
                zonesByLevel={this.state.zonesByLevel}
                isAvailable={
                  features.isEnabled('priceFitlers') &&
                  !!this.props.prices.length
                }
              />
              <BoxMinimumKey
                data={features.getOption('podSeats', 'minimumPerZones')}
                isAvailable={
                  features.isEnabled('podSeats') &&
                  features.getOption('podSeats', 'minimumPerZones').length &&
                  this.state.isEnhanced
                }
              />
              <SeatKey
                currentFacility={this.props.currentFacility}
                isAvailable={
                  features.isEnabled('seatKey') && this.state.isEnhanced
                }
              />
            </PanelContainer>
          </div>
        </div>
      </div>
    );
  }
});

export default SYOSContainer;



// WEBPACK FOOTER //
// ./src/javascripts/app/containers/SYOSContainer.js